/**
 * sl-sdk6.7.3: Roles Configuration Panel
 *
 * Matrix UI for configuring per-role permissions in a workspace.
 * Grouped by category (AI Features, Content, Administration).
 * Uses Switch components, loading/error states, toast notifications.
 * Owner column shown as always-on (not overridable).
 */
import { Loading, notify, Switch } from '@affine/component';
import { SettingHeader } from '@affine/component/setting-components';
import { useBackendAvailable } from '@affine/core/components/hooks/use-backend-availability';
import {
  GuardService,
  WorkspacePermissionService,
} from '@affine/core/modules/permissions';
import { WorkspaceService } from '@affine/core/modules/workspace';
import {
  isBackendUnavailableError,
  markBackendUnavailable,
} from '@affine/core/utils/backend-availability';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  SettingsOfflineHeaderAction,
  useSettingsOfflineIncompleteLabel,
} from '../../shared/settings-offline-status';
import { requestWorkspaceSettingGraphQL } from '../shared/workspace-settings-api';
import * as styles from './styles.css';

// GraphQL fetch helper (same pattern as bots/index.tsx — queries not in generated index)
async function gqlFetch<T>(
  workspaceId: string,
  query: string,
  variables: Record<string, unknown>
): Promise<T> {
  const res = await requestWorkspaceSettingGraphQL(
    workspaceId,
    query,
    variables
  );
  const json = await res.json();
  if (json.errors?.length) {
    throw new Error(json.errors[0].message);
  }
  return json.data;
}

type Role = string;

interface PermissionDefinition {
  key: string;
  category: string;
  featureKey?: string | null;
}

interface Override {
  id: string;
  role: string;
  permission: string;
  granted: boolean;
}

interface RoleDefaults {
  role: string;
  permissions: Record<string, boolean>;
}

interface RolePermissionMatrix {
  roles: Role[];
  ownerRole: Role;
  definitions: PermissionDefinition[];
  defaults: RoleDefaults[];
  overrides: Override[];
}

const QUERY_MATRIX = `
  query workspaceRolePermissionMatrix($workspaceId: String!) {
    workspaceRolePermissionMatrix(workspaceId: $workspaceId) {
      roles
      ownerRole
      definitions {
        key
        category
        featureKey
      }
      defaults {
        role
        permissions
      }
      overrides {
        id
        role
        permission
        granted
      }
    }
  }
`;

const MUTATION_UPDATE = `
  mutation updateRolePermission($input: UpdateRolePermissionInput!) {
    updateRolePermission(input: $input)
  }
`;

const MUTATION_RESET_ROLE = `
  mutation resetRolePermissions($workspaceId: String!, $role: Permission!) {
    resetRolePermissions(workspaceId: $workspaceId, role: $role)
  }
`;

export const RolesConfiguration = () => {
  const workspace = useService(WorkspaceService).workspace;
  const backendAvailable = useBackendAvailable();
  const offlineLabel = useSettingsOfflineIncompleteLabel();
  const workspaceId = workspace.id;
  const permissionService = useService(WorkspacePermissionService).permission;
  const guardService = useService(GuardService);
  const t = useI18n();

  const isOwner = useLiveData(permissionService.isOwner$);
  const isAdmin = useLiveData(permissionService.isAdmin$);
  const canEdit = Boolean(isOwner || isAdmin);

  const [matrix, setMatrix] = useState<RolePermissionMatrix | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<unknown | null>(null);
  const [updating, setUpdating] = useState<string | null>(null);

  const fetchMatrix = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    if (!backendAvailable) {
      setLoading(false);
      return;
    }
    try {
      const data = await gqlFetch<{
        workspaceRolePermissionMatrix: RolePermissionMatrix;
      }>(workspaceId, QUERY_MATRIX, { workspaceId });
      setMatrix(data.workspaceRolePermissionMatrix);
    } catch (error) {
      if (isBackendUnavailableError(error)) {
        markBackendUnavailable(error);
      }
      setMatrix(null);
      setLoadError(error);
    } finally {
      setLoading(false);
    }
  }, [backendAvailable, workspaceId]);

  const refreshPermissionCaches = useCallback(() => {
    permissionService.revalidate();
    guardService.revalidateCachedPermissions({ forceWorkspace: true });
  }, [guardService, permissionService]);

  useEffect(() => {
    void fetchMatrix();
  }, [fetchMatrix]);

  const defaultsByRole = useMemo(() => {
    const defaults = new Map<Role, Record<string, boolean>>();
    for (const entry of matrix?.defaults ?? []) {
      defaults.set(entry.role, entry.permissions ?? {});
    }
    return defaults;
  }, [matrix]);

  const getDefault = useCallback(
    (role: Role, permission: string): boolean =>
      defaultsByRole.get(role)?.[permission] ?? false,
    [defaultsByRole]
  );

  const getEffective = useCallback(
    (role: Role, permission: string): boolean => {
      const override = matrix?.overrides.find(
        o => o.role === role && o.permission === permission
      );
      if (override) return override.granted;
      return getDefault(role, permission);
    },
    [matrix, getDefault]
  );

  const isOverridden = useCallback(
    (role: Role, permission: string): boolean =>
      matrix?.overrides.some(
        o => o.role === role && o.permission === permission
      ) ?? false,
    [matrix]
  );

  const handleToggle = useCallback(
    async (role: Role, permission: string) => {
      const toggleKey = `${role}:${permission}`;
      setUpdating(toggleKey);
      try {
        const current = getEffective(role, permission);
        const defaultValue = getDefault(role, permission);
        const newValue = !current;
        const granted = newValue === defaultValue ? null : newValue;

        await gqlFetch(workspaceId, MUTATION_UPDATE, {
          input: { workspaceId, role, permission, granted },
        });
        await fetchMatrix();
        refreshPermissionCaches();
      } catch (error) {
        if (isBackendUnavailableError(error)) {
          markBackendUnavailable(error);
        }
        notify.error({
          title:
            t['com.streamlain.settings.workspace.roles.error-update']?.() ??
            'Failed to update permission',
        });
      } finally {
        setUpdating(null);
      }
    },
    [
      getEffective,
      getDefault,
      workspaceId,
      fetchMatrix,
      refreshPermissionCaches,
      t,
    ]
  );

  const handleResetRole = useCallback(
    async (role: Role) => {
      setUpdating(`reset:${role}`);
      try {
        await gqlFetch(workspaceId, MUTATION_RESET_ROLE, {
          workspaceId,
          role,
        });
        await fetchMatrix();
        refreshPermissionCaches();
        notify.success({
          title:
            t['com.streamlain.settings.workspace.roles.reset-success']?.() ??
            `${role} permissions reset to defaults`,
        });
      } catch (error) {
        if (isBackendUnavailableError(error)) {
          markBackendUnavailable(error);
        }
        notify.error({
          title:
            t['com.streamlain.settings.workspace.roles.error-reset']?.() ??
            'Failed to reset permissions',
        });
      } finally {
        setUpdating(null);
      }
    },
    [workspaceId, fetchMatrix, refreshPermissionCaches, t]
  );

  const categories = useMemo(() => {
    const grouped: Record<string, PermissionDefinition[]> = {};
    for (const def of matrix?.definitions ?? []) {
      (grouped[def.category] ??= []).push(def);
    }
    return Object.entries(grouped);
  }, [matrix]);

  const getPermissionLabel = (permission: string): string => {
    const key = `com.streamlain.settings.workspace.roles.permission.${permission}`;
    return (
      t[key]?.() ?? permission.replace('Workspace_', '').replace(/_/g, ' ')
    );
  };

  const showOfflineNotice =
    !backendAvailable ||
    (loadError !== null && isBackendUnavailableError(loadError));
  const header = (
    <SettingHeader
      title={
        t['com.streamlain.settings.workspace.roles.title']?.() ??
        'Roles Configuration'
      }
      subtitle={
        t['com.streamlain.settings.workspace.roles.description']?.() ??
        'Configure which permissions each role has in this workspace. Changes override the default settings. Owner permissions cannot be changed.'
      }
      action={<SettingsOfflineHeaderAction show={showOfflineNotice} />}
    />
  );

  if (loading && !showOfflineNotice) {
    return (
      <>
        {header}
        <div className={styles.loadingState}>
          <Loading size={20} />
          <span>
            {t['com.streamlain.settings.workspace.roles.loading']?.() ??
              'Loading role permissions...'}
          </span>
        </div>
      </>
    );
  }

  if (showOfflineNotice) {
    return (
      <>
        {header}
        <div className={styles.loadingState}>
          <span>{offlineLabel}</span>
        </div>
      </>
    );
  }

  if (loadError) {
    return (
      <>
        {header}
        <div className={styles.loadingState}>
          <span>
            {loadError instanceof Error
              ? loadError.message
              : (t['com.streamlain.settings.workspace.roles.load-error']?.() ??
                'Failed to load role permissions.')}
          </span>
        </div>
      </>
    );
  }

  return (
    <div data-testid="roles-configuration-panel" className={styles.rolesPanel}>
      {header}

      {/* Column headers — same cell widths as permission rows */}
      <div className={styles.headerRow}>
        <div className={styles.headerSpacer} />
        {matrix?.roles.map(role => (
          <div key={role} className={styles.headerCell}>
            <span className={styles.roleHeaderLabel}>{role}</span>
            {canEdit && (
              <button
                className={styles.resetButton}
                data-testid={`role-reset-${role}`}
                disabled={updating !== null}
                onClick={() => void handleResetRole(role)}
              >
                Reset
              </button>
            )}
          </div>
        ))}
        <div className={styles.headerCell}>
          <span className={styles.roleHeaderLabel} style={{ opacity: 0.4 }}>
            {matrix?.ownerRole ?? 'Owner'}
          </span>
        </div>
      </div>

      {/* Permission rows grouped by category */}
      {categories.map(([category, permissions]) => (
        <div key={category} className={styles.categorySection}>
          <div className={styles.categoryTitle}>
            {t[
              `com.streamlain.settings.workspace.roles.category.${category}`
            ]?.() ?? category}
          </div>
          {permissions.map(({ key: permission }) => (
            <div
              key={permission}
              className={styles.permissionRow}
              data-testid={`permission-row-${permission}`}
            >
              <span className={styles.permissionLabel}>
                {getPermissionLabel(permission)}
              </span>
              {matrix?.roles.map(role => {
                const effective = getEffective(role, permission);
                const overridden = isOverridden(role, permission);
                const toggleKey = `${role}:${permission}`;
                const isUpdating =
                  updating === toggleKey || updating === `reset:${role}`;

                return (
                  <div key={role} className={styles.toggleCell}>
                    <span className={styles.toggleWrapper}>
                      <Switch
                        data-testid={`role-toggle-${role}-${permission}`}
                        checked={effective}
                        disabled={!canEdit || isUpdating}
                        onChange={() => void handleToggle(role, permission)}
                      />
                      {overridden && <span className={styles.overrideDot} />}
                    </span>
                  </div>
                );
              })}
              <div className={styles.toggleCellOwner}>
                <Switch
                  checked={getDefault(matrix?.ownerRole ?? 'Owner', permission)}
                  disabled={true}
                />
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};
